import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Switch } from '../components/ui/switch';
import { 
  DollarSign, Plus, Calendar, Users, CheckCircle, Clock, 
  FileText, Loader2, Play, Eye, Trash2, Wallet, TrendingUp, Printer, Download, Edit, AlertCircle, Settings
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const ALLOWANCE_TYPES = [
  { value: 'housing', label: 'Housing' },
  { value: 'transport', label: 'Transport' },
  { value: 'medical', label: 'Medical' },
  { value: 'meal', label: 'Meal' },
  { value: 'phone', label: 'Phone' },
  { value: 'other', label: 'Other' }
];

const DEDUCTION_TYPES = [
  { value: 'tax', label: 'Tax' },
  { value: 'pension', label: 'Pension' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'loan', label: 'Loan' },
  { value: 'advance', label: 'Advance' },
  { value: 'other', label: 'Other' }
];

const Payroll = () => {
  const [summary, setSummary] = useState(null);
  const [salaries, setSalaries] = useState([]);
  const [payrolls, setPayrolls] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('salaries');
  const [officeCurrency, setOfficeCurrency] = useState({ code: 'USD', name: 'US Dollar', symbol: '$' });
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [showRunPayrollModal, setShowRunPayrollModal] = useState(false);
  const [showPayrollDetailModal, setShowPayrollDetailModal] = useState(false);
  const [showPayslipModal, setShowPayslipModal] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [payslipDetail, setPayslipDetail] = useState(null);
  const [loadingPayslip, setLoadingPayslip] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingSalary, setEditingSalary] = useState(null);
  
  const [salaryForm, setSalaryForm] = useState({
    employee_id: '',
    basic_salary: '',
    currency: 'USD',
    allowances: [],
    deductions: [],
    bank_name: '',
    account_number: ''
  });
  
  const [runPayrollForm, setRunPayrollForm] = useState({
    pay_period: `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`,
    pay_date: ''
  });

  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [validationError, setValidationError] = useState(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const [allowEmployeePayslipPrinting, setAllowEmployeePayslipPrinting] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  const { getAuthHeaders, currentOffice } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Only fetch data when we have the office context
    if (currentOffice?.id) {
      fetchData();
    } else {
      // No office selected, stop loading
      setLoading(false);
    }
  }, [currentOffice?.id]);

  // Set default currency when office currency is fetched
  useEffect(() => {
    if (officeCurrency?.code) {
      setSalaryForm(prev => ({ ...prev, currency: officeCurrency.code }));
    }
  }, [officeCurrency]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const officeId = currentOffice?.id;
      if (officeId) {
        headers['X-Office-ID'] = officeId;
      }

      // Fetch office currency first
      const currencyRes = await fetch(`${API_URL}/api/payroll/office-currency`, { headers });
      if (currencyRes.ok) {
        const currency = await currencyRes.json();
        setOfficeCurrency(currency);
      }

      // Fetch available currencies
      const currenciesRes = await fetch(`${API_URL}/api/payroll/currencies`, { headers });
      if (currenciesRes.ok) {
        setAvailableCurrencies(await currenciesRes.json());
      }

      const [summaryRes, salariesRes, payrollsRes, employeesRes, payslipsRes] = await Promise.all([
        fetch(`${API_URL}/api/payroll/summary`, { headers }),
        fetch(`${API_URL}/api/payroll/salaries`, { headers }),
        fetch(`${API_URL}/api/payroll/`, { headers }),
        fetch(`${API_URL}/api/employees/`, { headers }),
        fetch(`${API_URL}/api/payroll/office-payslips`, { headers })
      ]);

      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (salariesRes.ok) setSalaries(await salariesRes.json());
      if (payrollsRes.ok) setPayrolls(await payrollsRes.json());
      if (employeesRes.ok) setEmployees(await employeesRes.json());
      if (payslipsRes.ok) setPayslips(await payslipsRes.json());

      // Fetch payslip printing setting
      try {
        const settingsRes = await fetch(`${API_URL}/api/offices/my-office/settings`, { headers });
        if (settingsRes.ok) {
          const settingsData = await settingsRes.json();
          setAllowEmployeePayslipPrinting(settingsData.settings?.allow_employee_payslip_printing ?? true);
        }
      } catch (err) {
        // Settings endpoint may fail for non-office-admins, ignore
      }
    } catch (error) {
      console.error('Error fetching payroll data:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewPayslip = async (payslip) => {
    setSelectedPayslip(payslip);
    setLoadingPayslip(true);
    setShowPayslipModal(true);

    try {
      const headers = getAuthHeaders();
      const res = await fetch(
        `${API_URL}/api/payroll/payslip/${payslip.payroll_id}/${payslip.employee_id}`,
        { headers }
      );
      if (res.ok) {
        setPayslipDetail(await res.json());
      }
    } catch (error) {
      console.error('Error fetching payslip detail:', error);
    } finally {
      setLoadingPayslip(false);
    }
  };

  const printPayslip = () => {
    window.print();
  };

  const handlePayslipPrintingToggle = async (checked) => {
    setSavingSettings(true);
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/offices/my-office/settings`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ allow_employee_payslip_printing: checked }),
      });

      if (response.ok) {
        setAllowEmployeePayslipPrinting(checked);
        toast.success(checked ? 'Employees can now print payslips' : 'Payslip printing disabled for employees');
      } else {
        toast.error('Failed to update setting');
      }
    } catch (error) {
      console.error('Error updating setting:', error);
      toast.error('Failed to update setting');
    } finally {
      setSavingSettings(false);
    }
  };

  const getMonthName = (month) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    return months[month - 1] || '';
  };

  const handleCreateSalary = async () => {
    try {
      const headers = getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      const officeId = currentOffice?.id;
      if (officeId) {
        headers['X-Office-ID'] = officeId;
      }
      
      const response = await fetch(`${API_URL}/api/payroll/salaries`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...salaryForm,
          basic_salary: parseFloat(salaryForm.basic_salary) || 0
        })
      });
      
      if (response.ok) {
        setShowSalaryModal(false);
        resetSalaryForm();
        fetchData();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create salary');
      }
    } catch (error) {
      console.error('Error creating salary:', error);
      alert('Network error. Please try again.');
    }
  };

  const handleUpdateSalary = async () => {
    try {
      const headers = getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      const officeId = currentOffice?.id;
      if (officeId) {
        headers['X-Office-ID'] = officeId;
      }
      
      const response = await fetch(`${API_URL}/api/payroll/salaries/${salaryForm.employee_id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          basic_salary: parseFloat(salaryForm.basic_salary) || 0,
          currency: salaryForm.currency,
          allowances: salaryForm.allowances,
          deductions: salaryForm.deductions,
          bank_name: salaryForm.bank_name,
          account_number: salaryForm.account_number
        })
      });
      
      if (response.ok) {
        setShowSalaryModal(false);
        resetSalaryForm();
        fetchData();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update salary');
      }
    } catch (error) {
      console.error('Error updating salary:', error);
      alert('Network error. Please try again.');
    }
  };

  const handleSaveSalary = () => {
    if (isEditMode) {
      handleUpdateSalary();
    } else {
      handleCreateSalary();
    }
  };

  const handleRunPayroll = async () => {
    if (selectedEmployees.length === 0) {
      setValidationError({ message: 'Please select at least one employee to include in payroll' });
      return;
    }

    setValidationLoading(true);
    setValidationError(null);

    try {
      const headers = getAuthHeaders();
      const officeId = currentOffice?.id;
      if (officeId) {
        headers['X-Office-ID'] = officeId;
      }

      // First validate
      const validateResponse = await fetch(`${API_URL}/api/payroll/validate-run`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...runPayrollForm,
          employee_ids: selectedEmployees
        })
      });

      const validateResult = await validateResponse.json();

      if (!validateResponse.ok) {
        setValidationError({ message: validateResult.detail || 'Validation failed' });
        setValidationLoading(false);
        return;
      }

      if (!validateResult.valid) {
        setValidationError({
          message: validateResult.message,
          employees: validateResult.employees_with_records
        });
        setValidationLoading(false);
        return;
      }

      // If validation passed, run payroll
      const response = await fetch(`${API_URL}/api/payroll/run`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...runPayrollForm,
          employee_ids: selectedEmployees
        })
      });
      
      if (response.ok) {
        setShowRunPayrollModal(false);
        setRunPayrollForm({
          pay_period: `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`,
          pay_date: ''
        });
        setSelectedEmployees([]);
        setValidationError(null);
        fetchData();
      } else {
        const error = await response.json();
        setValidationError({ message: error.detail || 'Failed to run payroll' });
      }
    } catch (error) {
      console.error('Error running payroll:', error);
      setValidationError({ message: 'Network error. Please try again.' });
    } finally {
      setValidationLoading(false);
    }
  };

  const toggleEmployeeSelection = (employeeId) => {
    setSelectedEmployees(prev => 
      prev.includes(employeeId) 
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const toggleAllEmployees = () => {
    if (selectedEmployees.length === salaries.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(salaries.map(s => s.employee_id));
    }
  };

  const handleUpdatePayrollStatus = async (payrollId, newStatus) => {
    try {
      const headers = getAuthHeaders();
      const officeId = currentOffice?.id;
      if (officeId) {
        headers['X-Office-ID'] = officeId;
      }
      await fetch(`${API_URL}/api/payroll/${payrollId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status: newStatus })
      });
      fetchData();
    } catch (error) {
      console.error('Error updating payroll:', error);
    }
  };

  const handleDeleteSalary = async (employeeId) => {
    if (!confirm('Are you sure you want to delete this salary record?')) return;
    try {
      const headers = getAuthHeaders();
      const officeId = currentOffice?.id;
      if (officeId) {
        headers['X-Office-ID'] = officeId;
      }
      await fetch(`${API_URL}/api/payroll/salaries/${employeeId}`, {
        method: 'DELETE',
        headers
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting salary:', error);
    }
  };

  const resetSalaryForm = () => {
    setSalaryForm({
      employee_id: '',
      basic_salary: '',
      currency: getEffectiveCurrency(),
      allowances: [],
      deductions: [],
      bank_name: '',
      account_number: ''
    });
    setIsEditMode(false);
    setEditingSalary(null);
  };

  const openSalaryModal = () => {
    // Reset form with office's default currency
    setSalaryForm({
      employee_id: '',
      basic_salary: '',
      currency: getEffectiveCurrency(),
      allowances: [],
      deductions: [],
      bank_name: '',
      account_number: ''
    });
    setIsEditMode(false);
    setEditingSalary(null);
    setShowSalaryModal(true);
  };

  const openEditSalaryModal = (salary) => {
    // Populate form with existing salary data
    setSalaryForm({
      employee_id: salary.employee_id,
      basic_salary: salary.basic_salary?.toString() || '',
      currency: salary.currency || getEffectiveCurrency(),
      allowances: salary.allowances?.map(a => ({
        type: a.type || 'other',
        name: a.name || '',
        amount: a.amount || 0
      })) || [],
      deductions: salary.deductions?.map(d => ({
        type: d.type || 'other',
        name: d.name || '',
        amount: d.amount || 0
      })) || [],
      bank_name: salary.bank_name || '',
      account_number: salary.account_number || ''
    });
    setIsEditMode(true);
    setEditingSalary(salary);
    setShowSalaryModal(true);
  };

  const addAllowance = () => {
    setSalaryForm({
      ...salaryForm,
      allowances: [...salaryForm.allowances, { type: 'other', name: '', amount: 0 }]
    });
  };

  const updateAllowance = (index, field, value) => {
    const newAllowances = [...salaryForm.allowances];
    newAllowances[index][field] = field === 'amount' ? parseFloat(value) || 0 : value;
    setSalaryForm({ ...salaryForm, allowances: newAllowances });
  };

  const removeAllowance = (index) => {
    setSalaryForm({
      ...salaryForm,
      allowances: salaryForm.allowances.filter((_, i) => i !== index)
    });
  };

  const addDeduction = () => {
    setSalaryForm({
      ...salaryForm,
      deductions: [...salaryForm.deductions, { type: 'other', name: '', amount: 0 }]
    });
  };

  const updateDeduction = (index, field, value) => {
    const newDeductions = [...salaryForm.deductions];
    newDeductions[index][field] = field === 'amount' ? parseFloat(value) || 0 : value;
    setSalaryForm({ ...salaryForm, deductions: newDeductions });
  };

  const removeDeduction = (index) => {
    setSalaryForm({
      ...salaryForm,
      deductions: salaryForm.deductions.filter((_, i) => i !== index)
    });
  };

  const formatCurrency = (amount, currency) => {
    // Priority: passed currency > summary.currency > officeCurrency > 'USD'
    const currencyCode = currency || summary?.currency || officeCurrency?.code || 'USD';
    try {
      return new Intl.NumberFormat('en-US', { 
        style: 'currency', 
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount || 0);
    } catch {
      // Fallback for unsupported currencies
      return `${currencyCode} ${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
    }
  };

  // Get the effective currency for display
  const getEffectiveCurrency = () => {
    return summary?.currency || officeCurrency?.code || 'USD';
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return <Badge className={styles[status] || styles.draft}>{status}</Badge>;
  };

  // Delete draft payroll
  const handleDeletePayroll = async (payrollId, payPeriod) => {
    if (!confirm(`Are you sure you want to delete the draft payroll for ${payPeriod}? This action cannot be undone.`)) {
      return;
    }

    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) {
        headers['X-Office-ID'] = currentOffice.id;
      }
      
      const response = await fetch(`${API_URL}/api/payroll/${payrollId}`, {
        method: 'DELETE',
        headers
      });

      if (response.ok) {
        toast.success('Draft payroll deleted successfully');
        fetchPayrolls();
        fetchSummary();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete payroll');
      }
    } catch (error) {
      console.error('Error deleting payroll:', error);
      toast.error('Failed to delete payroll');
    }
  };

  // Get employees without salary records
  const employeesWithoutSalary = employees.filter(
    emp => !salaries.find(s => s.employee_id === (emp.id || emp.user_id))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!currentOffice?.id) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <AlertCircle className="h-12 w-12 text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">No Office Selected</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Please select an office to access payroll management.
        </p>
        <Button variant="outline" onClick={() => window.location.href = '/manage-offices'}>
          Go to Manage Offices
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payroll-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Payroll</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage salaries and run payroll</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openSalaryModal}>
            <Plus className="h-4 w-4 mr-2" />
            Add Salary
          </Button>
          <Button onClick={() => setShowRunPayrollModal(true)} data-testid="run-payroll-button">
            <Play className="h-4 w-4 mr-2" />
            Run Payroll
          </Button>
        </div>
      </div>

      {/* Stats */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm text-gray-600 mb-1">Last Payroll Amount</p>
                  <p className="text-sm sm:text-base md:text-lg font-bold leading-tight break-all">
                    {formatCurrency(summary.last_payroll_amount || 0, summary.currency)}
                  </p>
                  {summary.last_payroll_period && (
                    <p className="text-xs text-gray-500 mt-1">{summary.last_payroll_period}</p>
                  )}
                </div>
                <div className="p-2 sm:p-3 bg-green-100 rounded-lg flex-shrink-0">
                  <Wallet className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm text-gray-600 mb-1">Last Deductions</p>
                  <p className="text-sm sm:text-base md:text-lg font-bold leading-tight break-all">
                    {formatCurrency(summary.last_total_deductions || 0, summary.currency)}
                  </p>
                  {summary.last_payroll_period && (
                    <p className="text-xs text-gray-500 mt-1">{summary.last_payroll_period}</p>
                  )}
                </div>
                <div className="p-2 sm:p-3 bg-red-100 rounded-lg flex-shrink-0">
                  <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm text-gray-600 mb-1">Last Allowances</p>
                  <p className="text-sm sm:text-base md:text-lg font-bold leading-tight break-all">
                    {formatCurrency(summary.last_total_allowances || 0, summary.currency)}
                  </p>
                  {summary.last_payroll_period && (
                    <p className="text-xs text-gray-500 mt-1">{summary.last_payroll_period}</p>
                  )}
                </div>
                <div className="p-2 sm:p-3 bg-blue-100 rounded-lg flex-shrink-0">
                  <DollarSign className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="salaries">Salary Records ({salaries.length})</TabsTrigger>
          <TabsTrigger value="payrolls">Payroll History ({payrolls.length})</TabsTrigger>
          <TabsTrigger value="payslips">Salary Slips ({payslips.length})</TabsTrigger>
          <TabsTrigger value="settings" data-testid="payroll-settings-tab">
            <Settings className="h-4 w-4 mr-1" />
            Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="salaries">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Employee Salaries</CardTitle>
                {salaries.length > 0 && (
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-500">
                      {selectedEmployees.length} of {salaries.length} selected
                    </span>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={toggleAllEmployees}
                    >
                      {selectedEmployees.length === salaries.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {salaries.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-center py-3 px-2 font-medium w-12">
                          <input 
                            type="checkbox" 
                            checked={selectedEmployees.length === salaries.length && salaries.length > 0}
                            onChange={toggleAllEmployees}
                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </th>
                        <th className="text-left py-3 px-4 font-medium">Employee</th>
                        <th className="text-right py-3 px-4 font-medium">Basic</th>
                        <th className="text-right py-3 px-4 font-medium">Allowances</th>
                        <th className="text-right py-3 px-4 font-medium">Deductions</th>
                        <th className="text-right py-3 px-4 font-medium">Gross</th>
                        <th className="text-right py-3 px-4 font-medium">Net</th>
                        <th className="text-center py-3 px-4 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salaries.map((salary) => (
                        <tr 
                          key={salary.id} 
                          className={`border-b hover:bg-gray-50 dark:hover:bg-gray-800 ${selectedEmployees.includes(salary.employee_id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                        >
                          <td className="py-3 px-2 text-center">
                            <input 
                              type="checkbox" 
                              checked={selectedEmployees.includes(salary.employee_id)}
                              onChange={() => toggleEmployeeSelection(salary.employee_id)}
                              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                                {salary.employee_name?.split(' ').map(n => n[0]).join('') || '?'}
                              </div>
                              <div>
                                <p className="font-medium">{salary.employee_name}</p>
                                {salary.bank_name && (
                                  <p className="text-xs text-gray-500">{salary.bank_name}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-right">{formatCurrency(salary.basic_salary, salary.currency)}</td>
                          <td className="py-3 px-4 text-right text-green-600">+{formatCurrency(salary.total_allowances, salary.currency)}</td>
                          <td className="py-3 px-4 text-right text-red-600">-{formatCurrency(salary.total_deductions, salary.currency)}</td>
                          <td className="py-3 px-4 text-right">{formatCurrency(salary.gross_salary, salary.currency)}</td>
                          <td className="py-3 px-4 text-right font-bold">{formatCurrency(salary.net_salary, salary.currency)}</td>
                          <td className="py-3 px-4 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => openEditSalaryModal(salary)}
                                title="Edit Salary"
                                data-testid={`edit-salary-${salary.employee_id}`}
                              >
                                <Edit className="h-4 w-4 text-blue-500" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleDeleteSalary(salary.employee_id)}
                                title="Delete Salary"
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Wallet className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No salary records yet</p>
                  <Button variant="outline" className="mt-4" onClick={openSalaryModal}>
                    Add First Salary
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payrolls">
          <Card>
            <CardHeader>
              <CardTitle>Payroll History</CardTitle>
            </CardHeader>
            <CardContent>
              {payrolls.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-medium">Period</th>
                        <th className="text-center py-3 px-4 font-medium">Employees</th>
                        <th className="text-right py-3 px-4 font-medium">Total Net</th>
                        <th className="text-center py-3 px-4 font-medium">Status</th>
                        <th className="text-left py-3 px-4 font-medium">Pay Date</th>
                        <th className="text-center py-3 px-4 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payrolls.map((payroll) => (
                        <tr key={payroll.id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-800">
                          <td className="py-3 px-4 font-medium">{payroll.pay_period}</td>
                          <td className="py-3 px-4 text-center">{payroll.employee_count}</td>
                          <td className="py-3 px-4 text-right font-bold">{formatCurrency(payroll.total_net, payroll.currency)}</td>
                          <td className="py-3 px-4 text-center">{getStatusBadge(payroll.status)}</td>
                          <td className="py-3 px-4">{payroll.pay_date ? new Date(payroll.pay_date).toLocaleDateString() : '-'}</td>
                          <td className="py-3 px-4">
                            <div className="flex justify-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => { setSelectedPayroll(payroll); setShowPayrollDetailModal(true); }}>
                                <Eye className="h-4 w-4" />
                              </Button>
                              {payroll.status === 'draft' && (
                                <>
                                  <Button variant="ghost" size="sm" onClick={() => handleUpdatePayrollStatus(payroll.id, 'approved')} title="Approve">
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => handleDeletePayroll(payroll.id, payroll.pay_period)}
                                    title="Delete Draft"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                              {payroll.status === 'approved' && (
                                <Button variant="ghost" size="sm" onClick={() => handleUpdatePayrollStatus(payroll.id, 'paid')} title="Mark as Paid">
                                  <DollarSign className="h-4 w-4 text-green-600" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No payroll runs yet</p>
                  <Button variant="outline" className="mt-4" onClick={() => setShowRunPayrollModal(true)}>
                    Run First Payroll
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payslips">
          <Card>
            <CardHeader>
              <CardTitle>Salary Slips</CardTitle>
            </CardHeader>
            <CardContent>
              {payslips.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-medium">Employee</th>
                        <th className="text-left py-3 px-4 font-medium">Period</th>
                        <th className="text-right py-3 px-4 font-medium">Gross</th>
                        <th className="text-right py-3 px-4 font-medium">Deductions</th>
                        <th className="text-right py-3 px-4 font-medium">Net Pay</th>
                        <th className="text-center py-3 px-4 font-medium">Status</th>
                        <th className="text-center py-3 px-4 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payslips.map((slip, index) => (
                        <tr key={index} className="border-b hover:bg-gray-50 dark:hover:bg-gray-800">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                                {slip.employee_name?.split(' ').map(n => n[0]).join('') || '?'}
                              </div>
                              <span className="font-medium">{slip.employee_name}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4">{slip.pay_period}</td>
                          <td className="py-3 px-4 text-right">{formatCurrency(slip.gross_salary, slip.currency)}</td>
                          <td className="py-3 px-4 text-right text-red-600">-{formatCurrency(slip.total_deductions, slip.currency)}</td>
                          <td className="py-3 px-4 text-right font-bold text-green-600">{formatCurrency(slip.net_salary, slip.currency)}</td>
                          <td className="py-3 px-4 text-center">{getStatusBadge(slip.payroll_status)}</td>
                          <td className="py-3 px-4 text-center">
                            <Button variant="outline" size="sm" onClick={() => viewPayslip(slip)}>
                              <Eye className="h-4 w-4 mr-1" />
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No salary slips available yet</p>
                  <p className="text-sm text-gray-400 mt-2">Run payroll to generate salary slips</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Payroll Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Payslip Printing Control */}
              <div className="flex items-center justify-between p-4 rounded-lg border bg-gray-50 dark:bg-gray-800">
                <div className="space-y-1">
                  <Label className="text-base font-medium flex items-center gap-2">
                    <Printer className="h-4 w-4" />
                    Allow Employees to Print Payslips
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    When enabled, employees can print their payslips from "My Salary" page.
                    When disabled, only admins can print payslips.
                  </p>
                </div>
                <Switch
                  checked={allowEmployeePayslipPrinting}
                  onCheckedChange={handlePayslipPrintingToggle}
                  disabled={savingSettings}
                  data-testid="payslip-printing-toggle"
                />
              </div>

              {/* Status indicator */}
              <Alert className={allowEmployeePayslipPrinting ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}>
                <CheckCircle className={`h-4 w-4 ${allowEmployeePayslipPrinting ? 'text-green-600' : 'text-amber-600'}`} />
                <AlertDescription className={allowEmployeePayslipPrinting ? 'text-green-700' : 'text-amber-700'}>
                  {allowEmployeePayslipPrinting
                    ? 'Employees can view and print their payslips.'
                    : 'Employees can view but cannot print their payslips. Only admins can print.'}
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Salary Modal */}
      <Dialog open={showSalaryModal} onOpenChange={setShowSalaryModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditMode ? 'Edit Employee Salary' : 'Add Employee Salary'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Employee *</Label>
              {isEditMode ? (
                <Input value={editingSalary?.employee_name || 'Employee'} disabled className="bg-gray-100" />
              ) : employeesWithoutSalary.length === 0 ? (
                <div className="space-y-2">
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      {employees.length === 0 
                        ? 'No employees found for this office. Please add employees first.'
                        : 'All employees already have salary records.'}
                    </AlertDescription>
                  </Alert>
                  {employees.length === 0 && (
                    <Button variant="outline" size="sm" onClick={fetchData} className="w-full">
                      <Loader2 className="h-4 w-4 mr-2" />
                      Refresh Employee List
                    </Button>
                  )}
                </div>
              ) : (
                <Select value={salaryForm.employee_id} onValueChange={(v) => setSalaryForm({...salaryForm, employee_id: v})}>
                  <SelectTrigger data-testid="salary-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>
                    {employeesWithoutSalary.map((emp) => (
                      <SelectItem key={emp.id || emp.user_id} value={emp.id || emp.user_id}>
                        {emp.first_name} {emp.last_name} {emp.employment?.employee_id ? `(${emp.employment.employee_id})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Basic Salary *</Label>
                <Input type="number" value={salaryForm.basic_salary} onChange={(e) => setSalaryForm({...salaryForm, basic_salary: e.target.value})} placeholder="0.00" />
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={salaryForm.currency} onValueChange={(v) => setSalaryForm({...salaryForm, currency: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {availableCurrencies.length > 0 ? (
                      availableCurrencies.map(c => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.code} - {c.name}
                        </SelectItem>
                      ))
                    ) : (
                      <>
                        <SelectItem value="KES">KES - Kenyan Shilling</SelectItem>
                        <SelectItem value="USD">USD - US Dollar</SelectItem>
                        <SelectItem value="EUR">EUR - Euro</SelectItem>
                        <SelectItem value="GBP">GBP - British Pound</SelectItem>
                        <SelectItem value="UGX">UGX - Ugandan Shilling</SelectItem>
                        <SelectItem value="TZS">TZS - Tanzanian Shilling</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Allowances */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Allowances</Label>
                <Button variant="outline" size="sm" onClick={addAllowance}><Plus className="h-4 w-4 mr-1" />Add</Button>
              </div>
              {salaryForm.allowances.map((allowance, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <Select value={allowance.type} onValueChange={(v) => updateAllowance(index, 'type', v)}>
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ALLOWANCE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input placeholder="Name" value={allowance.name} onChange={(e) => updateAllowance(index, 'name', e.target.value)} className="flex-1" />
                  <Input type="number" placeholder="Amount" value={allowance.amount} onChange={(e) => updateAllowance(index, 'amount', e.target.value)} className="w-28" />
                  <Button variant="ghost" size="sm" onClick={() => removeAllowance(index)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              ))}
            </div>
            
            {/* Deductions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Deductions</Label>
                <Button variant="outline" size="sm" onClick={addDeduction}><Plus className="h-4 w-4 mr-1" />Add</Button>
              </div>
              {salaryForm.deductions.map((deduction, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <Select value={deduction.type} onValueChange={(v) => updateDeduction(index, 'type', v)}>
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DEDUCTION_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input placeholder="Name" value={deduction.name} onChange={(e) => updateDeduction(index, 'name', e.target.value)} className="flex-1" />
                  <Input type="number" placeholder="Amount" value={deduction.amount} onChange={(e) => updateDeduction(index, 'amount', e.target.value)} className="w-28" />
                  <Button variant="ghost" size="sm" onClick={() => removeDeduction(index)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              ))}
            </div>
            
            {/* Bank Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Bank Name</Label>
                <Input value={salaryForm.bank_name} onChange={(e) => setSalaryForm({...salaryForm, bank_name: e.target.value})} placeholder="e.g., NMB Bank" />
              </div>
              <div>
                <Label>Account Number</Label>
                <Input value={salaryForm.account_number} onChange={(e) => setSalaryForm({...salaryForm, account_number: e.target.value})} placeholder="Account number" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowSalaryModal(false); resetSalaryForm(); }}>Cancel</Button>
            <Button 
              onClick={handleSaveSalary} 
              disabled={!salaryForm.employee_id || !salaryForm.basic_salary}
              data-testid="save-salary-button"
            >
              {isEditMode ? 'Update Salary' : 'Add Salary'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Run Payroll Modal */}
      <Dialog open={showRunPayrollModal} onOpenChange={(open) => {
        setShowRunPayrollModal(open);
        if (!open) setValidationError(null);
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Run Payroll</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {validationError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <p className="font-medium">{validationError.message}</p>
                  {validationError.employees && validationError.employees.length > 0 && (
                    <div className="mt-2 max-h-32 overflow-y-auto">
                      <p className="text-sm font-medium mb-1">Employees with existing records:</p>
                      <ul className="list-disc list-inside text-sm space-y-1">
                        {validationError.employees.map((emp, idx) => (
                          <li key={idx}>{emp.employee_name || emp.employee_id}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}
            
            <div>
              <Label>Pay Period *</Label>
              <Input type="month" value={runPayrollForm.pay_period} onChange={(e) => setRunPayrollForm({...runPayrollForm, pay_period: e.target.value})} />
            </div>
            <div>
              <Label>Pay Date</Label>
              <Input type="date" value={runPayrollForm.pay_date} onChange={(e) => setRunPayrollForm({...runPayrollForm, pay_date: e.target.value})} />
            </div>
            
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-blue-900 dark:text-blue-100">Selected Employees</span>
              </div>
              {selectedEmployees.length > 0 ? (
                <>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    <strong>{selectedEmployees.length}</strong> employee{selectedEmployees.length !== 1 ? 's' : ''} will be included in this payroll run.
                  </p>
                  <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                    Estimated total: <strong>{formatCurrency(
                      salaries
                        .filter(s => selectedEmployees.includes(s.employee_id))
                        .reduce((sum, s) => sum + (s.net_salary || 0), 0),
                      getEffectiveCurrency()
                    )}</strong>
                  </p>
                </>
              ) : (
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  No employees selected. Please select employees from the salary records table.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRunPayrollModal(false); setValidationError(null); }}>Cancel</Button>
            <Button 
              onClick={handleRunPayroll} 
              disabled={!runPayrollForm.pay_period || selectedEmployees.length === 0 || validationLoading}
            >
              {validationLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Validating...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Run Payroll ({selectedEmployees.length})
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payroll Detail Modal */}
      <Dialog open={showPayrollDetailModal} onOpenChange={setShowPayrollDetailModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Payroll Detail - {selectedPayroll?.pay_period}</DialogTitle>
          </DialogHeader>
          {selectedPayroll && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-xs text-gray-500">Total Gross</p>
                  <p className="text-lg font-bold">{formatCurrency(selectedPayroll.total_gross, selectedPayroll.currency)}</p>
                </div>
                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-xs text-gray-500">Total Deductions</p>
                  <p className="text-lg font-bold text-red-600">-{formatCurrency(selectedPayroll.total_deductions, selectedPayroll.currency)}</p>
                </div>
                <div className="p-3 bg-green-50 dark:bg-green-950 rounded-lg">
                  <p className="text-xs text-gray-500">Total Net</p>
                  <p className="text-lg font-bold text-green-600">{formatCurrency(selectedPayroll.total_net, selectedPayroll.currency)}</p>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3">Employee</th>
                      <th className="text-right py-2 px-3">Basic</th>
                      <th className="text-right py-2 px-3">Allowances</th>
                      <th className="text-right py-2 px-3">Deductions</th>
                      <th className="text-right py-2 px-3">Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPayroll.items?.map((item, index) => (
                      <tr key={index} className="border-b">
                        <td className="py-2 px-3 font-medium">{item.employee_name}</td>
                        <td className="py-2 px-3 text-right">{formatCurrency(item.basic_salary, selectedPayroll.currency)}</td>
                        <td className="py-2 px-3 text-right text-green-600">+{formatCurrency(item.total_allowances, selectedPayroll.currency)}</td>
                        <td className="py-2 px-3 text-right text-red-600">-{formatCurrency(item.total_deductions, selectedPayroll.currency)}</td>
                        <td className="py-2 px-3 text-right font-bold">{formatCurrency(item.net_salary, selectedPayroll.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayrollDetailModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payslip Detail Modal */}
      <Dialog open={showPayslipModal} onOpenChange={setShowPayslipModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto print:max-w-full print:max-h-full">
          <DialogHeader className="print:hidden">
            <DialogTitle>
              Salary Slip - {selectedPayslip && selectedPayslip.pay_period}
            </DialogTitle>
          </DialogHeader>
          
          {loadingPayslip ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : payslipDetail ? (
            <div className="space-y-6" id="payslip-content">
              {/* Header */}
              <div className="border-b pb-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-xl font-bold">{payslipDetail.office?.company_name || payslipDetail.office?.name}</h2>
                    <p className="text-sm text-gray-500">{payslipDetail.office?.location}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">SALARY SLIP</p>
                    <p className="text-sm text-gray-500">
                      {getMonthName(payslipDetail.period_month)} {payslipDetail.period_year}
                    </p>
                  </div>
                </div>
              </div>

              {/* Employee Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div>
                  <p className="text-xs text-gray-500">Employee Name</p>
                  <p className="font-medium">{payslipDetail.employee?.name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Pay Date</p>
                  <p className="font-medium">
                    {payslipDetail.pay_date ? new Date(payslipDetail.pay_date).toLocaleDateString() : '-'}
                  </p>
                </div>
              </div>

              {/* Earnings & Deductions */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Earnings */}
                <div>
                  <h3 className="font-semibold mb-3 text-green-700">Earnings</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between py-1">
                      <span>Basic Salary</span>
                      <span>{formatCurrency(payslipDetail.earnings?.basic_salary, payslipDetail.currency)}</span>
                    </div>
                    {payslipDetail.earnings?.allowances?.map((a, i) => (
                      <div key={i} className="flex justify-between py-1 text-sm">
                        <span className="capitalize">{a.name || a.type}</span>
                        <span>{formatCurrency(a.amount, payslipDetail.currency)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between py-2 border-t font-semibold">
                      <span>Gross Earnings</span>
                      <span>{formatCurrency(payslipDetail.earnings?.gross_salary, payslipDetail.currency)}</span>
                    </div>
                  </div>
                </div>

                {/* Deductions */}
                <div>
                  <h3 className="font-semibold mb-3 text-red-700">Deductions</h3>
                  <div className="space-y-2">
                    {payslipDetail.deductions?.items?.length > 0 ? (
                      payslipDetail.deductions.items.map((d, i) => (
                        <div key={i} className="flex justify-between py-1 text-sm">
                          <span className="capitalize">{d.name || d.type}</span>
                          <span>{formatCurrency(d.amount, payslipDetail.currency)}</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500">No deductions</p>
                    )}
                    <div className="flex justify-between py-2 border-t font-semibold">
                      <span>Total Deductions</span>
                      <span>{formatCurrency(payslipDetail.deductions?.total_deductions, payslipDetail.currency)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Net Pay */}
              <div className="p-4 bg-green-50 dark:bg-green-950 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold">Net Pay</span>
                  <span className="text-2xl font-bold text-green-700">
                    {formatCurrency(payslipDetail.net_salary, payslipDetail.currency)}
                  </span>
                </div>
              </div>

              {/* Bank Details */}
              {(payslipDetail.bank_details?.bank_name || payslipDetail.bank_details?.account_number) && (
                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <h3 className="font-semibold mb-2">Payment Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Bank</p>
                      <p>{payslipDetail.bank_details?.bank_name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Account</p>
                      <p>{payslipDetail.bank_details?.account_number || '-'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Approval Info */}
              <div className="text-xs text-gray-500 flex justify-between">
                <span>Prepared by: {payslipDetail.prepared_by || '-'}</span>
                <span>Approved by: {payslipDetail.approved_by || '-'}</span>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>Failed to load salary slip details</p>
            </div>
          )}
          <DialogFooter className="print:hidden">
            <Button variant="outline" onClick={() => setShowPayslipModal(false)}>Close</Button>
            {payslipDetail && (
              <Button onClick={printPayslip}>
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Payroll;
